import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  Chip,
  IconButton,
  Pagination,
  CircularProgress,
  Divider,
  Button,
  Avatar,
  Stack,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Transaction {
  id: number;
  wallet: number;
  user_id: number;
  user_name: string;
  type: "CREDIT" | "DEBIT";
  source_type: "BOOKING_COMMISSION" | "REFERRAL_COMMISSION" | "PAYOUT";
  source_id: string;
  amount: string;
  status: "PENDING" | "COMPLETED" | "FAILED" | "REVERSED";
  description: string;
  created_at: string;
}

interface PayoutRequest {
  id: number;
  amount: string;
  method: string;
  status: string;
  requested_at: string;
  completed_at: string | null;
  admin_note: string | null;
  payout_account_snapshot: {
    method: string;
    masked_ecocash_number?: string;
    bank_name?: string;
    masked_account_number?: string;
  };
  payment_proofs: {
    id: number;
    file: string;
    note: string | null;
    uploaded_at: string;
  }[];
}

interface PaginatedResponse {
  results: Transaction[];
  count: number;
  next: string | null;
  previous: string | null;
}

interface PayoutPaginatedResponse {
  results: PayoutRequest[];
  count: number;
}

interface TransactionsHistoryModalProps {
  open: boolean;
  onClose: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const TAB_FILTERS = ["ALL", "EARNINGS", "PAYOUTS"] as const;
type TabFilter = (typeof TAB_FILTERS)[number];

const PAGE_SIZE = 10;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const formatFullDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const statusStyles = (status: Transaction["status"]) => {
  if (status === "COMPLETED")
    return { bgcolor: "#e8f5e9", color: "#4caf50", label: "Complete" };
  if (status === "PENDING")
    return { bgcolor: "#fff8e1", color: "#ff9800", label: "Pending" };
  if (status === "FAILED")
    return { bgcolor: "#ffebee", color: "#f44336", label: "Failed" };
  return { bgcolor: "#f5f5f5", color: "#888", label: "Reversed" };
};

const accountSummary = (
  snapshot: PayoutRequest["payout_account_snapshot"],
): string => {
  if (snapshot.method === "ECOCASH")
    return `Ecocash — ${snapshot.masked_ecocash_number ?? ""}`;
  return `${snapshot.bank_name ?? "Bank"} — ${snapshot.masked_account_number ?? ""}`;
};

// ── Detail Row ────────────────────────────────────────────────────────────────

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1,
    }}
  >
    <Typography variant="body2" sx={{ color: "#888", fontSize: "13px" }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        fontSize: "13px",
        textAlign: "right",
        maxWidth: "60%",
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ── Payout Detail Panel ───────────────────────────────────────────────────────

function PayoutDetailPanel({
  transaction,
  onBack,
  color400,
  color10,
}: {
  transaction: Transaction;
  onBack: () => void;
  color400: string;
  color10: string;
}) {
  const [payout, setPayout] = useState<PayoutRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(false);
      try {
        // Fetch all partner payout requests and find matching source_id
        const res = await apiService.get<PayoutPaginatedResponse>(
          `/api/wallet/payout-requests/?page_size=100`,
        );
        const match = (res.data.results ?? []).find(
          (p) => String(p.id) === String(transaction.source_id),
        );
        setPayout(match ?? null);
        if (!match) setError(true);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [transaction.source_id]);

  const { bgcolor, color, label } = statusStyles(transaction.status);

  return (
    <Box>
      {/* Sub-header */}
      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: 2,
          background: `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`,
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <IconButton
            size="small"
            onClick={onBack}
            sx={{ color: "rgba(255,255,255,0.8)", p: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} fontSize="14px">
            Payout Details
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: "rgba(255,255,255,0.15)",
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            <ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              -${parseFloat(transaction.amount).toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {formatFullDate(transaction.created_at)}
            </Typography>
          </Box>
          <Box sx={{ ml: "auto !important" }}>
            <Chip
              label={label}
              size="small"
              sx={{
                bgcolor,
                color,
                fontWeight: 600,
                fontSize: "0.65rem",
                height: 20,
              }}
            />
          </Box>
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error || !payout ? (
          <Box sx={{ textAlign: "center", py: 4, color: "#888" }}>
            <Typography variant="body2">
              Could not load payout details.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Payout info */}
            <DetailRow label="Method" value={payout.method} />
            <Divider />
            <DetailRow
              label="Account"
              value={accountSummary(payout.payout_account_snapshot)}
            />
            <Divider />
            <DetailRow
              label="Requested"
              value={formatFullDate(payout.requested_at)}
            />
            {payout.completed_at && (
              <>
                <Divider />
                <DetailRow
                  label="Completed"
                  value={formatFullDate(payout.completed_at)}
                />
              </>
            )}
            {payout.admin_note && (
              <>
                <Divider />
                <DetailRow
                  label="Admin Note"
                  value={
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "italic",
                        color: "#555",
                        fontSize: "12px",
                      }}
                    >
                      {payout.admin_note}
                    </Typography>
                  }
                />
              </>
            )}

            {/* Payment Proofs */}
            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#888",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontWeight: 600,
                  display: "block",
                  mb: 1,
                }}
              >
                Payment Proofs
              </Typography>

              {payout.payment_proofs.length === 0 ? (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#fafafa",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#aaa", fontSize: "12px" }}
                  >
                    No payment proof uploaded yet.
                  </Typography>
                </Box>
              ) : (
                payout.payment_proofs.map((proof) => {
                  const isImage = /\.(png|jpg|jpeg|webp)$/i.test(proof.file);
                  return (
                    <Box
                      key={proof.id}
                      sx={{
                        mb: 1.5,
                        border: "1px solid #f0f0f0",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      {/* Image preview */}
                      {isImage && (
                        <Box
                          component="img"
                          src={proof.file}
                          alt="Payment proof"
                          sx={{
                            width: "100%",
                            maxHeight: 200,
                            objectFit: "contain",
                            bgcolor: "#fafafa",
                            display: "block",
                          }}
                        />
                      )}

                      {/* Proof meta + view link */}
                      <Box
                        sx={{
                          p: 1.5,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          bgcolor: "#fafafa",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "12px", fontWeight: 500 }}
                          >
                            {proof.note || "Payment proof"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#aaa", fontSize: "11px" }}
                          >
                            {formatFullDate(proof.uploaded_at)}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          href={proof.file}
                          target="_blank"
                          sx={{
                            textTransform: "none",
                            fontSize: "11px",
                            color: color400,
                            flexShrink: 0,
                          }}
                        >
                          {isImage ? "Open" : "View PDF"}
                        </Button>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function TransactionsHistoryModal({
  open,
  onClose,
}: TransactionsHistoryModalProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color400 = reduxTheme.primary_colors?.[400] ?? "#724E41";
  const color10 = reduxTheme.primary_colors?.[10] ?? "#f5ede9";

  const [tabValue, setTabValue] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);

  const activeFilter: TabFilter = TAB_FILTERS[tabValue];

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const res = await apiService.get<PaginatedResponse>(
        `/api/wallet/transactions/?limit=${PAGE_SIZE}&offset=${offset}`,
      );
      setTransactions(res.data.results ?? []);
      setTotalCount(res.data.count ?? 0);
    } catch (err) {
      console.error("Failed to load transactions", err as Error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (open) fetchTransactions();
  }, [open, fetchTransactions]);

  // Reset detail view when modal closes
  useEffect(() => {
    if (!open) setSelectedTransaction(null);
  }, [open]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
    setSelectedTransaction(null);
  };

  const handleTransactionClick = (t: Transaction) => {
    // Only PAYOUT + COMPLETED transactions are clickable for proof
    if (t.source_type === "PAYOUT" && t.status === "COMPLETED") {
      setSelectedTransaction(t);
    }
  };

  const filteredTransactions = transactions.filter((t) => {
    if (activeFilter === "ALL") return true;
    if (activeFilter === "EARNINGS")
      return (
        t.source_type === "BOOKING_COMMISSION" ||
        t.source_type === "REFERRAL_COMMISSION"
      );
    if (activeFilter === "PAYOUTS") return t.source_type === "PAYOUT";
    return true;
  });

  const pageCount = Math.ceil(totalCount / PAGE_SIZE);
  const isDetailView = selectedTransaction !== null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        setSelectedTransaction(null);
        onClose();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0px 8px 40px rgba(0,0,0,0.12)",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* ── Detail view ── */}
        {isDetailView ? (
          <>
            {/* Close button stays accessible */}
            <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedTransaction(null);
                  onClose();
                }}
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: "auto", maxHeight: "80vh", pb: 3 }}>
              <PayoutDetailPanel
                transaction={selectedTransaction}
                onBack={() => setSelectedTransaction(null)}
                color400={color400}
                color10={color10}
              />
            </Box>
          </>
        ) : (
          <>
            {/* ── List view ── */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                pt: 3,
                pb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "1.1rem" }}
              >
                Transaction History
              </Typography>
              <IconButton onClick={onClose} size="small" sx={{ color: "#666" }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Tabs */}
            <Box sx={{ px: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  minHeight: 36,
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTabs-flexContainer": { gap: 1 },
                }}
              >
                {["All", "Earnings", "Payouts"].map((label, index) => (
                  <Tab
                    key={label}
                    label={label}
                    sx={{
                      minHeight: 32,
                      minWidth: "auto",
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      color: tabValue === index ? "#fff" : "#666",
                      bgcolor: tabValue === index ? color400 : "#f5f5f5",
                      "&:hover": {
                        bgcolor: tabValue === index ? color400 : "#e0e0e0",
                      },
                      "&.Mui-selected": { color: "#fff" },
                    }}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Content */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : filteredTransactions.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, color: "#888" }}>
                <Typography variant="body2">No transactions found.</Typography>
              </Box>
            ) : (
              <List sx={{ px: 2, py: 2 }}>
                {filteredTransactions.map((t) => {
                  const { bgcolor, color, label } = statusStyles(t.status);
                  const isCredit = t.type === "CREDIT";
                  const isClickable =
                    t.source_type === "PAYOUT" && t.status === "COMPLETED";

                  return (
                    <ListItem
                      key={t.id}
                      onClick={() => handleTransactionClick(t)}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        py: 1.5,
                        px: 1,
                        borderRadius: 2,
                        cursor: isClickable ? "pointer" : "default",
                        "&:hover": {
                          bgcolor: isClickable ? "#f5f0eb" : "#fafafa",
                        },
                        transition: "background 0.15s",
                      }}
                    >
                      {/* Direction icon */}
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          bgcolor: isCredit ? "#e8f5e9" : "#fff3e0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      >
                        {isCredit ? (
                          <ArrowDownwardIcon
                            sx={{ fontSize: 16, color: "#4caf50" }}
                          />
                        ) : (
                          <ArrowUpwardIcon
                            sx={{ fontSize: 16, color: "#ff9800" }}
                          />
                        )}
                      </Box>

                      {/* Description */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "#1a1a1a",
                            fontSize: "0.9rem",
                          }}
                        >
                          {isCredit ? "+" : "-"}$
                          {parseFloat(t.amount).toFixed(2)}
                        </Typography>
                        <Typography
                          sx={{ color: "#888", fontSize: "0.8rem", mt: 0.25 }}
                        >
                          {t.description}
                        </Typography>
                        {/* Hint for clickable rows */}
                        {isClickable && (
                          <Typography
                            sx={{
                              color: color400,
                              fontSize: "0.72rem",
                              mt: 0.25,
                              fontWeight: 500,
                            }}
                          >
                            Tap to view payment proof
                          </Typography>
                        )}
                      </Box>

                      {/* Status + Date */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={label}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            bgcolor,
                            color,
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                        <Typography sx={{ color: "#999", fontSize: "0.75rem" }}>
                          {formatDate(t.created_at)}
                        </Typography>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {/* Pagination */}
            {pageCount > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  px: 3,
                  pb: 2,
                }}
              >
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, val) => setPage(val)}
                  size="small"
                  sx={{
                    "& .MuiPaginationItem-root": { fontSize: "0.75rem" },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
